import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Input,
    Label,
    ListBox,
    Select,
    Switch,
    TextField,
} from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { MIN_PASSWORD_LENGTH, ROLE_LABEL_KEY } from '../teamMeta';
import type { BranchOption, UseUserFormResult } from './useUserForm';

type UserFormModalViewProps = {
    mode: 'create' | 'edit';
    isOpen: boolean;
    branchOptions: BranchOption[];
    isSelf: boolean;
    error: string | null;
    isSubmitting: boolean;
    onClose: () => void;
    form: UseUserFormResult;
};

/**
 * Render-only Create / Edit user modal. Which fields show is presentational
 * gating driven by the form's role matrix; all field state lives in
 * {@link useUserForm}, wired in by the `UserFormModal` container.
 */
export const UserFormModalView: React.FC<UserFormModalViewProps> = ({
    mode,
    isOpen,
    branchOptions,
    isSelf,
    error,
    isSubmitting,
    onClose,
    form: f,
}) => {
    const { t } = useTranslation('pages');

    const lockedBranchName = React.useMemo(
        () =>
            branchOptions.find((b) => b.id === f.branchId)?.name ??
            f.branchId ??
            '',
        [branchOptions, f.branchId],
    );

    const roleEditable = mode === 'create' || f.canReassign;
    const showRoleSelect = roleEditable && f.assignableRoles.length > 0;
    const showBranch = f.fields.branch && (mode === 'create' || f.canReassign);
    const showEmail = mode === 'create' && f.fields.email;
    const showPassword = mode === 'create' && f.fields.password;
    const showPin = mode === 'create' && f.fields.pin;

    return (
        <FormDrawer
            footer={
                <>
                    <Button
                        isDisabled={isSubmitting}
                        onPress={onClose}
                        variant="tertiary"
                        {...getNodeTestIdProp('user-form-cancel')}
                    >
                        {t('team.components.userTable.form.cancel')}
                    </Button>
                    <Button
                        isDisabled={!f.isValid}
                        isPending={isSubmitting}
                        onPress={f.onSubmit}
                        variant="primary"
                        {...getNodeTestIdProp('user-form-submit')}
                    >
                        {mode === 'edit'
                            ? t('team.components.userTable.form.editSubmit')
                            : t('team.components.userTable.form.submit')}
                    </Button>
                </>
            }
            isOpen={isOpen}
            onClose={onClose}
            subtitle={t('team.components.userTable.form.description')}
            testId="user-form"
            title={
                mode === 'edit'
                    ? t('team.components.userTable.form.editTitle')
                    : t('team.components.userTable.form.title')
            }
        >
            {showRoleSelect ? (
                <Select
                    fullWidth
                    onChange={(next) => f.onRoleChange(String(next))}
                    value={f.role}
                    variant="secondary"
                >
                    <Label>
                        {t('team.components.userTable.form.roleLabel')}
                    </Label>
                    <Select.Trigger
                        {...getNodeTestIdProp('user-form-role-trigger')}
                    >
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            {f.assignableRoles.map((role) => (
                                <ListBox.Item
                                    id={role}
                                    key={role}
                                    textValue={t(ROLE_LABEL_KEY[role])}
                                    {...getNodeTestIdProp(
                                        `user-form-role-option-${role}`,
                                    )}
                                >
                                    {t(ROLE_LABEL_KEY[role])}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Select.Popover>
                </Select>
            ) : mode === 'edit' ? (
                <div
                    className="flex flex-col gap-1"
                    {...getNodeTestIdProp('user-form-role-static')}
                >
                    <span className="text-sm font-medium text-foreground">
                        {t('team.components.userTable.form.roleLabel')}
                    </span>
                    <span className="text-sm text-muted">
                        {t(ROLE_LABEL_KEY[f.role])}
                    </span>
                </div>
            ) : null}

            <TextField
                fullWidth
                isRequired
                onChange={f.onNameChange}
                value={f.name}
                {...getNodeTestIdProp('user-form-name')}
            >
                <Label>{t('team.components.userTable.form.nameLabel')}</Label>
                <Input
                    placeholder={t(
                        'team.components.userTable.form.namePlaceholder',
                    )}
                />
            </TextField>

            {showBranch ? (
                f.branchLocked ? (
                    <div
                        className="flex flex-col gap-1"
                        {...getNodeTestIdProp('user-form-branch-locked')}
                    >
                        <span className="text-sm font-medium text-foreground">
                            {t('team.components.userTable.form.branchLabel')}
                        </span>
                        <span className="text-sm text-muted">
                            {lockedBranchName}
                        </span>
                    </div>
                ) : (
                    <Select
                        fullWidth
                        onChange={(next) => f.onBranchChange(String(next))}
                        placeholder={t(
                            'team.components.userTable.form.branchPlaceholder',
                        )}
                        value={f.branchId}
                        variant="secondary"
                    >
                        <Label>
                            {t('team.components.userTable.form.branchLabel')}
                        </Label>
                        <Select.Trigger
                            {...getNodeTestIdProp('user-form-branch-trigger')}
                        >
                            <Select.Value />
                            <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                            <ListBox>
                                {branchOptions.map((branch) => (
                                    <ListBox.Item
                                        id={branch.id}
                                        key={branch.id}
                                        textValue={branch.name}
                                        {...getNodeTestIdProp(
                                            `user-form-branch-option-${branch.id}`,
                                        )}
                                    >
                                        {branch.name}
                                        <ListBox.ItemIndicator />
                                    </ListBox.Item>
                                ))}
                            </ListBox>
                        </Select.Popover>
                    </Select>
                )
            ) : null}

            {showEmail ? (
                <TextField
                    autoComplete="off"
                    fullWidth
                    isRequired
                    onChange={f.onEmailChange}
                    type="email"
                    value={f.email}
                    {...getNodeTestIdProp('user-form-email')}
                >
                    <Label>
                        {t('team.components.userTable.form.emailLabel')}
                    </Label>
                    <Input
                        placeholder={t(
                            'team.components.userTable.form.emailPlaceholder',
                        )}
                    />
                </TextField>
            ) : null}

            {showPassword ? (
                <TextField
                    autoComplete="new-password"
                    fullWidth
                    isRequired
                    onChange={f.onPasswordChange}
                    type="password"
                    value={f.password}
                    {...getNodeTestIdProp('user-form-password')}
                >
                    <Label>
                        {t('team.components.userTable.form.passwordLabel')}
                    </Label>
                    <Input
                        placeholder={t(
                            'team.components.userTable.form.passwordPlaceholder',
                            { min: MIN_PASSWORD_LENGTH },
                        )}
                        type="password"
                    />
                    <p
                        className={
                            f.passwordErrorKey
                                ? 'text-xs text-danger'
                                : 'text-xs text-muted'
                        }
                        {...getNodeTestIdProp('user-form-password-hint')}
                    >
                        {t(
                            f.passwordErrorKey ??
                                'team.components.userTable.form.passwordHelper',
                            { min: MIN_PASSWORD_LENGTH },
                        )}
                    </p>
                </TextField>
            ) : null}

            {showPin ? (
                <TextField
                    fullWidth
                    onChange={f.onPinChange}
                    value={f.pin}
                    {...getNodeTestIdProp('user-form-pin')}
                >
                    <Label>
                        {t('team.components.userTable.form.pinLabel')}
                    </Label>
                    <Input
                        inputMode="numeric"
                        placeholder={t(
                            'team.components.userTable.form.pinPlaceholder',
                        )}
                    />
                    <p
                        className={
                            f.pinErrorKey
                                ? 'text-xs text-danger'
                                : 'text-xs text-muted'
                        }
                        {...getNodeTestIdProp('user-form-pin-hint')}
                    >
                        {t(
                            f.pinErrorKey ??
                                'team.components.userTable.form.pinHelper',
                        )}
                    </p>
                </TextField>
            ) : null}

            {mode === 'edit' ? (
                <div
                    className="flex flex-col gap-1.5"
                    {...getNodeTestIdProp('user-form-status-field')}
                >
                    <Switch
                        isDisabled={isSelf}
                        isSelected={f.status === 'Active'}
                        onChange={f.onStatusToggle}
                        {...getNodeTestIdProp('user-form-status')}
                    >
                        <Switch.Control>
                            <Switch.Thumb />
                        </Switch.Control>
                        <Switch.Content>
                            <Label className="text-sm font-medium text-foreground">
                                {t(
                                    'team.components.userTable.form.activeLabel',
                                )}
                            </Label>
                        </Switch.Content>
                    </Switch>
                    <p className="text-sm text-muted">
                        {isSelf
                            ? t('team.components.userTable.form.selfHelper')
                            : t('team.components.userTable.form.activeHelper')}
                    </p>
                </div>
            ) : null}

            {error ? (
                <p
                    className="text-sm text-danger"
                    role="alert"
                    {...getNodeTestIdProp('user-form-error')}
                >
                    {error}
                </p>
            ) : null}
        </FormDrawer>
    );
};
